import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { environment } from '../../environments/environment';

interface MobileUpdatePolicy {
  androidMinimumVersionCode?: number;
  iosMinimumVersion?: string;
  androidStoreUrl?: string;
  iosStoreUrl?: string;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class ForceUpdateService {
  private static readonly POLICY_URL = '/book-now/v1/mobile-app/update-policy';
  private static readonly ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bookingdexter.app';
  private static readonly IOS_STORE_URL = 'https://apps.apple.com/search?term=BookingDexter';

  private required = false;
  private policy: MobileUpdatePolicy = {};

  constructor(private http: HttpClient) {}

  async initialize(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const policy = await this.http.get<MobileUpdatePolicy>(
        `${environment.apiBaseUrl}${ForceUpdateService.POLICY_URL}`
      ).toPromise();
      const appInfo = await CapacitorApp.getInfo();
      this.policy = policy || {};
      const platform = Capacitor.getPlatform();

      this.required = platform === 'android'
        ? Number(appInfo.build) < Number(policy.androidMinimumVersionCode || 0)
        : platform === 'ios' && this.compareVersions(appInfo.version, policy.iosMinimumVersion || '0') < 0;
    } catch (error) {
      // A temporary policy-service outage must not lock users out of the app.
      console.warn('Unable to check for a required app update', error);
    }

    return this.required;
  }

  isRequired(): boolean {
    return this.required;
  }

  getMessage(): string {
    return this.policy.message || 'A critical update is required to continue using BookingDexter.';
  }

  async openStore(): Promise<void> {
    const storeUrl = Capacitor.getPlatform() === 'ios'
      ? this.policy.iosStoreUrl || ForceUpdateService.IOS_STORE_URL
      : this.policy.androidStoreUrl || ForceUpdateService.ANDROID_STORE_URL;
    await Browser.open({ url: storeUrl });
  }

  private compareVersions(current: string, minimum: string): number {
    const currentParts = current.split('.').map(part => Number(part) || 0);
    const minimumParts = minimum.split('.').map(part => Number(part) || 0);

    for (let index = 0; index < Math.max(currentParts.length, minimumParts.length); index += 1) {
      const difference = (currentParts[index] || 0) - (minimumParts[index] || 0);
      if (difference !== 0) {
        return difference;
      }
    }

    return 0;
  }
}
